<template>
  <v-container fluid class="sci-page-wrapper pa-0" style="font-family: 'Inter', sans-serif;">
    <div class="p-4 md:p-8">
      <div class="max-w-7xl mx-auto space-y-8">
        <!-- HEADER -->
      <div class="sci-page-header pb-4">
          <h1 class="sci-page-title-only">Faturamento</h1>
          <p class="text-sm text-slate-500 font-medium mt-1">Gestão e controle de faturamentos e emissões</p>
        </div>

        <!-- ÁREA DE PESQUISA -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
            <v-icon color="orange" small>mdi-filter-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
              OPÇÕES DE FILTRAGEM
            </h2>
          </div>
          <div class="p-8">
            <v-row dense class="items-end">
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Município</label>
                <v-autocomplete
                  :disabled="userGroup == 1 ? false : true"
                  :items="prefectures"
                  placeholder="Selecione o município"
                  v-model="filter.prefecture_id"
                  item-value="prefecture_id"
                  item-text="prefecture_social_name"
                  outlined dense hide-details="auto"
                  class="bg-slate-50 rounded-lg"
                  @change="
                    [
                      getVehicles(filter.prefecture_id),
                      getProviders(filter.prefecture_id),
                    ]
                  "
                  no-data-text="Nenhum município encontrado"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Data Inicial</label>
                <Date
                  @dateValue="[(filter.startDate = $event)]"
                  label="Início"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Data Final</label>
                <Date @dateValue="[(filter.endDate = $event)]" label="Fim" />
              </v-col>
              <v-col cols="12" md="4">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Participante</label>
                <v-autocomplete
                  :items="providers"
                  placeholder="Selecione o fornecedor"
                  v-model="filter.provider_id"
                  item-value="provider_id"
                  item-text="provider_name"
                  outlined dense hide-details="auto"
                  class="bg-slate-50 rounded-lg"
                  no-data-text="Nenhum fornecedor encontrado"
                  clearable
                />
              </v-col>
              <v-col cols="12" md="8">
                <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 ml-1">Veículo</label>
                <v-autocomplete
                  :items="vehicles"
                  placeholder="Todos os veículos"
                  v-model="filter.vehicle_id"
                  item-value="vehicle_id"
                  item-text="vehicle_board"
                  clearable
                  outlined dense hide-details="auto"
                  class="bg-slate-50 rounded-lg"
                  no-data-text="Nenhum veículo encontrado"
                >
                  <template slot="selection" slot-scope="{ item }"
                    >{{ item.brand_desc }} - {{ item.model_desc }} -
                    {{ item.vehicle_board }}</template
                  >
                  <template slot="item" slot-scope="{ item }"
                    >{{ item.brand_desc }} - {{ item.model_desc }} -
                    {{ item.vehicle_board }}</template
                  >
                </v-autocomplete>
              </v-col>
            </v-row>
            <div class="flex flex-wrap justify-end gap-3 mt-8">
              <button
                @click="search"
                :disabled="!(filter.provider_id && filter.startDate && filter.endDate) || loader"
                class="bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white rounded-lg shadow-md shadow-orange-500/20 flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 font-bold text-[11px] uppercase tracking-widest"
              >
                <v-icon color="white" small>mdi-filter-variant</v-icon>
                {{ loader ? 'FILTRANDO...' : 'FILTRAR' }}
              </button>
              <v-btn
                color="#333333"
                dark
                depressed
                style="color: white !important;"
                class="rounded-lg font-bold text-[11px] tracking-widest h-10 px-8 uppercase"
                target="_blank"
                :to="`/billings/report/all/${filter.startDate}/${filter.endDate}/${filter.prefecture_id}/${filter.provider_id}/${filter.vehicle_id || 0}`"
                :disabled="!(filter.provider_id && filter.startDate && filter.endDate) || loader"
              >
                <v-icon left small color="orange">mdi-file-pdf-box</v-icon>
                GERAR PDF
              </v-btn>
              <button
                @click="[(showData = true), searchBillings()]"
                :disabled="loader"
                class="bg-slate-800 hover:bg-slate-900 text-white rounded-lg shadow-md flex items-center justify-center gap-2 w-fit whitespace-nowrap px-8 h-10 transition-all transform hover:-translate-y-0.5 font-bold text-[11px] uppercase tracking-widest disabled:opacity-50"
              >
                <v-icon color="white" small>mdi-receipt-text-check</v-icon>
                VER FATURAMENTOS
              </button>
            </div>
          </div>
        </section>

        <!-- ÁREA DE RESULTADOS -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
          <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
            <div class="flex items-center gap-3">
              <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
              <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                REGISTROS ENCONTRADOS
              </h2>
            </div>
            <button
              @click="invoice"
              :disabled="!(filter.filterDate.length > 0) || loader"
              class="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-5 py-2 rounded-lg border border-white/20 transition-all uppercase tracking-widest flex items-center gap-2 disabled:opacity-50"
            >
              <v-icon small color="white">mdi-check-all</v-icon>
              FATURAR SELECIONADOS
            </button>
          </div>
          <div class="p-0">
            <div class="overflow-x-auto">
              <div class="min-w-[1100px]">
            <v-data-table
              :loading="loader"
              :items="filter.filterDate"
              :headers="conferenceHeaders"
              class="custom-table"
              hide-default-footer
              :page.sync="page"
              :items-per-page="itemsPerPage"
              @page-count="pageCount = $event"
            >
              <template v-slot:item.datehour="{ item }">
                <span class="text-xs text-slate-600 font-semibold">{{ convertDateTime(item.datehour) }}</span>
              </template>
              <template v-slot:item.supply_adjusted="{ item }">
                <span v-if="item.supply_adjusted" class="text-[10px] font-black px-2 py-1 rounded bg-blue-50 text-blue-600 uppercase tracking-wider">SIM</span>
                <span v-else class="text-[10px] font-bold px-2 py-1 rounded bg-slate-50 text-slate-400 uppercase tracking-wider italic">NÃO</span>
              </template>
              <template v-slot:item.supply_status="{ item }">
                <span v-if="item.supply_status" class="text-[10px] font-black px-2 py-1 rounded bg-emerald-50 text-emerald-600 uppercase tracking-wider">CONCLUÍDO</span>
                <span v-else class="text-[10px] font-bold px-2 py-1 rounded bg-orange-50 text-orange-600 uppercase tracking-wider italic">ABERTO</span>
              </template>
              <template v-slot:item.model_desc="{ item }">
                <div class="flex flex-col py-2">
                  <span class="text-xs font-bold text-slate-800">{{ item.vehicle_board }}</span>
                  <span class="text-[10px] text-slate-500 font-medium uppercase truncate max-w-[150px]">{{ item.brand_desc || '' }} - {{ item.model_desc || '' }}</span>
                </div>
              </template>
              <template v-slot:item.supply_extra="{ item }">
                <span v-if="item.supply_extra" class="text-[10px] font-black px-2 py-1 rounded bg-rose-50 text-rose-600 uppercase tracking-wider text-center min-w-[30px]">SIM</span>
                <span v-else class="text-[10px] font-bold px-2 py-1 rounded bg-slate-50 text-slate-400 uppercase tracking-wider italic text-center min-w-[30px]">NÃO</span>
              </template>
              <template v-slot:item.supply_confered="{ item }">
                <span v-if="item.supply_confered" class="text-[10px] font-black px-2 py-1 rounded bg-emerald-50 text-emerald-600 uppercase tracking-wider text-center min-w-[30px]">SIM</span>
                <span v-else class="text-[10px] font-bold px-2 py-1 rounded bg-slate-50 text-slate-400 uppercase tracking-wider italic text-center min-w-[30px]">NÃO</span>
              </template>
              <template v-slot:item.supply_billed="{ item }">
                <span v-if="item.supply_billed" class="text-[10px] font-black px-2 py-1 rounded bg-indigo-50 text-indigo-600 uppercase tracking-wider text-center min-w-[30px]">SIM</span>
                <span v-else class="text-[10px] font-bold px-2 py-1 rounded bg-slate-50 text-slate-400 uppercase tracking-wider italic text-center min-w-[30px]">NÃO</span>
              </template>
              <template v-slot:item.supply_liters_stocked="{ item }">
                <span class="font-black text-slate-700">{{ realMaskII(item.supply_liters_stocked) }}</span>
              </template>
              <template v-slot:item.item_billing_price="{ item }">
                <span class="font-semibold text-slate-600">{{ realMaskII(item.supply_bidding_price) }}</span>
              </template>
              <template v-slot:item.total_billing_price="{ item }">
                <span class="font-black text-slate-900">R$ {{
                  item.supply_bidding_price && item.supply_liters_stocked
                    ? realMask(
                        (
                          Number(item.supply_bidding_price || 0) * Number(item.supply_liters_stocked || 0)
                        ).toFixed(2)
                      )
                    : 0
                }}</span>
              </template>
              <template v-slot:item.action="{ item }">
                <div class="flex items-center justify-end gap-2">
                  <button
                    v-if="!item.supply_billed"
                    @click="openModal(item.supply_id)"
                    class="p-2 rounded-lg text-slate-400 hover:bg-orange-50 hover:text-orange-500 transition-all"
                    title="Corrigir"
                  >
                    <v-icon small color="currentColor">mdi-square-edit-outline</v-icon>
                  </button>
                </div>
              </template>
            </v-data-table>

                </div>
              </div>

            <!-- Custom Pagination Footer -->
            <div class="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center w-full">
                <div class="flex-1 text-left">
                  <span class="text-xs text-slate-500 font-medium whitespace-nowrap">Exibindo {{ paginationStart }} de {{ filter.filterDate.length }} registros</span>
                </div>
                
                <div class="flex-1 flex justify-end gap-1">
                    <button 
                      @click="page > 1 ? page-- : null" 
                      :disabled="page === 1"
                      class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs text-slate-500 font-medium"
                    >
                      Anterior
                    </button>
                    
                    <button 
                      v-for="p in visiblePages" 
                      :key="p"
                      @click="page = p"
                      :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                      class="px-3 py-1.5 border rounded-md transition-colors text-xs font-medium"
                    >
                      {{ p }}
                    </button>

                    <button 
                      @click="page < pageCount ? page++ : null" 
                      :disabled="page === pageCount"
                      class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs text-slate-500 font-medium"
                    >
                      Próximo
                    </button>
                </div>
            </div>

            <!-- FOOTER COM TOTAIS -->
            <div class="p-8 bg-slate-50 border-t border-slate-200 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div class="bg-orange-50/50 p-6 rounded-2xl border border-orange-100 shadow-sm flex items-center justify-between">
                <div>
                  <span class="text-[10px] font-black text-orange-400 uppercase tracking-[0.2em] block mb-1">TOTAL LICITADO</span>
                  <span class="text-2xl font-black text-orange-600">R$ {{ totals.bidding_total ? realMask(totals.bidding_total.toFixed(2)) : '0,00' }}</span>
                </div>
                <v-icon size="40" color="orange-100" style="opacity:0.5">mdi-currency-usd</v-icon>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
    <!-- DIALOG FATURAMENTOS -->
    <v-dialog v-model="showData" width="1000">
      <v-card class="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
        <div class="px-8 py-5 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-receipt-text-check</v-icon>
            <h3 class="text-[11px] font-black text-white uppercase tracking-[0.2em]">Faturamentos</h3>
          </div>
          <button @click="showData = false" class="text-white hover:text-orange-400 transition-colors bg-white/10 p-1.5 rounded-lg">
            <v-icon size="18" color="currentColor">mdi-close</v-icon>
          </button>
        </div>
        <v-card-text class="p-0">
          <v-data-table 
            :headers="headersBillings" 
            :items="dataBillings"
            class="custom-table"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.bill_nfe_emission="{ item }">
              <span class="text-xs text-slate-600 font-semibold">{{
                item.bill_nfe_emission
                  ? convertDateTime(item.bill_nfe_emission)
                  : "Não Informado"
              }}</span>
            </template>
            <template v-slot:item.bill_start_date="{ item }">
              <span class="text-xs text-slate-700 font-bold uppercase tracking-tight">
                De {{ convertDate(item.bill_start_date) }} até
                {{ convertDate(item.bill_end_date) }}
              </span>
            </template>
            <template v-slot:item.bill_total_value="{ item }">
              <span class="font-black text-slate-800">R$ {{ realMask(Number(item.bill_total_value || 0).toFixed(2)) }}</span>
            </template>
            <template v-slot:item.bill_total_updated="{ item }">
              <span class="text-orange-600 font-black">R$ {{ realMask(Number(item.bill_total_updated || 0).toFixed(2)) }}</span>
            </template>
            <template v-slot:item.sec_name="{ item }">
              <div class="flex flex-col">
                <span class="font-bold text-slate-700 text-xs uppercase">{{ item.sec_name }}</span>
                <span class="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">{{ item.subsec_desc || 'GERAL' }}</span>
              </div>
            </template>
            <template v-slot:item.bill_interest="{ item }">
              <span class="text-xs text-slate-600">{{
                item.bill_interest ? item.bill_interest : "Não Informado"
              }}</span>
            </template>
            <template v-slot:item.bill_nfe_number="{ item }">
              <span class="text-xs text-slate-600 font-bold">{{
                item.bill_nfe_number ? item.bill_nfe_number : "Não Informado"
              }}</span>
            </template>

            <template v-slot:item.action="{ item }">
              <div class="flex items-center justify-end gap-2">
                <button 
                  v-if="!item.bill_nfe"
                  @click="$router.push(item.vehicle_id ? `/billings/report/single/${filter.startDate}/${filter.endDate}/${filter.prefecture_id}/${filter.provider_id}/${item.vehicle_id}` : `/billings/report/single/${filter.startDate}/${filter.endDate}/${filter.prefecture_id}/${filter.provider_id}/${item.vehicle_id}/${item.sec_id}`)"
                  class="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-all"
                  title="Visualizar"
                >
                  <v-icon size="18" color="currentColor">mdi-printer-outline</v-icon>
                </button>
                <button 
                  v-else
                  @click="$router.push(`/billings/report/nfe/${filter.startDate}/${filter.endDate}/${filter.prefecture_id}/${filter.provider_id}`)"
                  class="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-all"
                  title="Visualizar NFe"
                >
                  <v-icon size="18" color="currentColor">mdi-printer-outline</v-icon>
                </button>
              </div>
            </template>
          </v-data-table>
        </v-card-text>
        <div class="px-8 py-5 bg-slate-50 flex items-center justify-between border-t border-slate-200">
          <div class="flex flex-col">
            <span class="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">VALOR TOTAL ACUMULADO</span>
            <span class="text-xl font-black text-slate-800 tracking-tight">R$ {{ totals.billing_price ? realMask(totals.billing_price.toFixed(2)) : '0,00' }}</span>
          </div>
          <div class="flex gap-3">
            <button
              @click="printAllBillings"
              :disabled="!(dataBillings.length > 0)"
              class="px-6 py-2 text-[10px] font-black text-slate-600 hover:bg-slate-100 rounded-lg transition-colors uppercase tracking-[0.2em] flex items-center gap-2"
            >
              <v-icon size="16" color="orange">mdi-printer-settings</v-icon>
              RELATÓRIO GERAL
            </button>
            <button
              @click="rollbackInvoice"
              :disabled="!(dataBillings.length > 0)"
              class="px-6 py-2 text-[10px] font-black text-rose-500 hover:bg-rose-50 rounded-lg transition-colors uppercase tracking-[0.2em]"
            >
              DESFAZER FATURAMENTO
            </button>
            <button @click="showData = false" class="bg-slate-800 hover:bg-slate-900 text-white font-black text-[10px] uppercase tracking-[0.2em] px-8 py-3 rounded-xl transition-all">
              FECHAR
            </button>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- DIALOG ALTERAR ABASTECIMENTO -->
    <v-dialog v-model="dialog" width="600" persistent>
      <v-card class="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl">
        <div class="px-8 py-5 flex items-center justify-between" style="background-color: #333333;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-pencil-box-outline</v-icon>
            <h3 class="text-[11px] font-black text-white uppercase tracking-[0.2em]">Alterar Abastecimento</h3>
          </div>
          <button @click="dialog = false" class="text-white hover:text-orange-400 transition-colors bg-white/10 p-1.5 rounded-lg">
            <v-icon size="18" color="currentColor">mdi-close</v-icon>
          </button>
        </div>
        <v-card-text class="py-10 px-8">
          <v-form ref="form">
            <v-row>
              <v-col cols="12">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Data e Hora do Registro</label>
                <v-text-field
                  v-mask="'00/00/0000 00:00'"
                  v-model="dataAdjust.datehour"
                  solo flat dense hide-details
                  class="custom-field-form"
                />
              </v-col>
              <v-col cols="12">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Veículo</label>
                <v-autocomplete
                  placeholder="SELECIONE O VEÍCULO..."
                  :items="vehiclesDialog"
                  item-value="vehicle_id"
                  v-model="dataAdjust.vehicle_id"
                  solo flat dense hide-details
                  class="custom-field-form"
                >
                  <template slot="selection" slot-scope="{ item }"
                    >{{ item.brand_desc }} - {{ item.model_desc }} -
                    {{ item.vehicle_board }}</template
                  >
                  <template slot="item" slot-scope="{ item }"
                    >{{ item.brand_desc }} - {{ item.model_desc }} -
                    {{ item.vehicle_board }}</template
                  >
                </v-autocomplete>
              </v-col>

              <v-col cols="12">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Item Licitado</label>
                <v-autocomplete
                  placeholder="SELECIONE O ITEM..."
                  :items="items"
                  item-value="item_id"
                  item-text="item_description_desc"
                  v-model="dataAdjust.item_id"
                  solo flat dense hide-details
                  class="custom-field-form"
                >
                  <template slot="selection" slot-scope="{ item }"
                    >{{ item.item_description_desc }} — R$ {{ item.item_price ? realMaskII(item.item_price.toFixed(3)) : '0,000' }}</template
                  >
                  <template slot="item" slot-scope="{ item }"
                    >{{ item.item_description_desc }} — R$ {{ item.item_price ? realMaskII(item.item_price.toFixed(3)) : '0,000' }}</template
                  >
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Quantidade (Litros)</label>
                <v-text-field
                  v-model="dataAdjust.supply_liters_stocked"
                  v-mask-decimal.br="3"
                  solo flat dense hide-details
                  class="custom-field-form"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Preço Licitado</label>
                <v-text-field
                  v-model="dataAdjust.supply_bidding_price"
                  v-mask-decimal.br="3"
                  solo flat dense hide-details
                  class="custom-field-form"
                />
              </v-col>
              <v-col cols="12" md="6">
                <label class="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Cupom / NF-e</label>
                <v-text-field
                  v-model="dataAdjust.supply_coupon"
                  solo flat dense hide-details
                  class="custom-field-form"
                  placeholder="Nº DO DOCUMENTO"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <div class="px-8 py-5 bg-slate-50 flex justify-end gap-3 border-t border-slate-100">
          <button @click="dialog = false" class="px-6 py-2 text-[10px] font-black text-slate-400 hover:text-slate-600 transition-colors uppercase tracking-[0.2em]">Cancelar</button>
          <button @click="ajust" :loading="loader" class="bg-slate-800 hover:bg-slate-950 text-white font-black text-[10px] uppercase tracking-[0.2em] px-10 py-3.5 rounded-xl shadow-lg flex items-center gap-2 group transition-all">
             <v-icon v-if="loader" small class="animate-spin text-white">mdi-loading</v-icon>
             <span>CONFIRMAR AJUSTE</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snack.show"
      :color="snack.color"
      timeout="4000"
      top
      elevation="24"
      class="custom-snack"
    >
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="font-bold text-xs uppercase tracking-wider">{{ snack.message }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn dark text v-bind="attrs" @click="snack.show = false" class="bg-white/10 ml-4 rounded">
          <v-icon size="16">mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import Helpers from "@/mixins/helpers.js";
import Date from "@/components/InputDate.vue";
export default {
  components: { Date },
  mixins: [Helpers],
  data() {
    return {
      userGroup: null,
      loader: true,
      dialog: false,
      showSearch: false,
      showData: false,
      showPrint: false,
      showPrintPageOne: false,
      showConferencePageOne: false,
      data: [],
      providers: [],
      vehicles: [],
      vehiclesDialog: [],
      prefectures_sec: [],
      prefectures_subsectors: [],
      dataBillings: [],
      prefecture: [],
      prefectures: [],
      items: [],
      provider: [],
      dataReport: [],
      dataAdjust: {
        supply_id: null,
        vehicle_id: null,
        provider_id: null,
        item_id: null,
        supply_liters_stocked: 0,
        supply_price_day: 0,
        supply_coupon: null,
        supply_bidding_price: null,
      },
      totals: {
        bidding_total: 0,
        day_value: 0,
        billing_price: 0,
      },
      snack: {
        show: false,
        message: "",
        color: "",
      },
      filter: {
        endDate: "",
        startDate: "",

        provider_id: null,
        prefecture_id: null,
        vehicle_id: null,
        filterDate: [],
      },
      conferenceHeaders: [
        {
          text: "Data/Hora",
          align: "start",
          value: "datehour",
        },
        { text: "Veículo", value: "model_desc" },
        { text: "Item", value: "item_description_desc" },
        { text: "Lts Abastecidos", value: "supply_liters_stocked" },
        { text: "Preço Licitado", value: "supply_bidding_price" },
        { text: "T. Licitado", value: "total_billing_price" },
        { text: "Status", value: "supply_status" },
        { text: "Bloqueado", value: "supply_extra" },
        { text: "Conferido", value: "supply_confered" },
        { text: "Faturado", value: "supply_billed" },
        { text: "Cupom", value: "supply_coupon" },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
      headersBillings: [
        {
          text: "De",
          align: "start",
          value: "bill_start_date",
          sortable: false,
        },
        { text: "Setor / Ficha Contábil", value: "sec_name", sortable: false },
        { text: "N.Empenho", value: "bill_interest", sortable: false },
        { text: "N.Nfe", value: "bill_nfe_number", sortable: false },
        { text: "Dt.Emi.Nfe", value: "bill_nfe_emission", sortable: false },
        { text: "Vl.Nfe", value: "bill_total_value", sortable: false },
        { text: "Vl.Atualizado", value: "bill_total_updated", sortable: false },
        { text: "Status", value: "bill_status", sortable: false },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
      headers: [
        {
          text: "Nome",
          align: "start",
          sortable: false,
          value: "driver_name",
        },
        { text: "CPF", value: "driver_cpf" },
        { text: "Celular", value: "driver_cel" },
        { text: "Ações", align: "right", value: "action", sortable: false },
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    paginationStart() {
      return (this.page - 1) * this.itemsPerPage + 1;
    },
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);

      if (end - start + 1 < totalVisible) {
        start = Math.max(1, end - totalVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  created() {
    this.Helpers;
    this.initialize();
    this.userGroup = JSON.parse(sessionStorage.getItem("userData")).group_id;
  },
  methods: {
    openModal(id) {
      this.$http.get(`/supplies/single/${id}`).then((r) => {
        this.dataAdjust = r.data[0];
        this.dataAdjust.supply_liters_stocked = Number(
          r.data[0].supply_liters_stocked || 0
        ).toFixed(3);
        this.dataAdjust.supply_price_day = Number(
          r.data[0].supply_price_day || 0
        ).toFixed(3);
        this.dataAdjust.supply_bidding_price = Number(
          r.data[0].supply_bidding_price || 0
        ).toFixed(3);
        this.dataAdjust.datehour = this.convertDateTime(r.data[0].datehour);
        this.$http
          .get(`/vehicles/supplies/${this.filter.prefecture_id}`)
          .then((v) => {
            this.vehiclesDialog = v.data;
            this.$http
              .get(
                `/biddings/items/${this.filter.prefecture_id}/${this.filter.provider_id}`
              )
              .then((i) => {
                this.items = i.data;
                this.dialog = true;
              });
          });
      });
    },
    ajust() {
      delete this.dataAdjust.supply_authorized_liters;
      delete this.dataAdjust.supply_authorized_value;
      this.dataAdjust.supply_adjusted = true;
      this.$http
        .put(`/supplies/single/${this.dataAdjust.supply_id}`, this.dataAdjust)
        .then((r) => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.loader = true;
          setTimeout(() => {
            this.dialog = false;
            this.search();
          }, 1500);
        })
        .catch((e) => {
          this.snack.show = true;
          this.snack.message = e.message;
          this.snack.color = "error";
          this.loader = false;
          this.$store.dispatch("setLoader", false);
        });
    },
    search() {
      this.totals.day_value = 0;
      this.totals.bidding_total = 0;
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http.post("/supplies/repairvalues").then(() => {
        this.$http
          .post("/supplies/billings", this.filter)
          .then((r) => {
            const searchData = r.data;
            let liters = 0;
            let bidding_price = 0;
            let supply_price_day = 0;
            searchData.map((supply, index) => {
              liters = Number(supply.supply_liters_stocked || 0).toFixed(3);
              bidding_price = Number(supply.supply_bidding_price || 0).toFixed(3);
              supply_price_day = Number(supply.supply_price_day || 0).toFixed(3);
              const val = Number(supply.supply_value || 0).toFixed(2);
              searchData[index].supply_liters_stocked = liters;
              searchData[index].supply_bidding_price = bidding_price;
              searchData[index].supply_price_day = supply_price_day;
              searchData[index].supply_value = val;
              this.totals.day_value += parseFloat(supply.totalDayValue || 0);
              this.totals.bidding_total += parseFloat(supply.totalBiddingValue || 0);
            });
            this.filter.filterDate = searchData;
            this.searchBillings();
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
          })
          .catch((e) => {
            this.loader = false;
            this.$store.dispatch("setLoader", this.loader);
            alert(e.message);
          });
      });
    },
    loadReport(vehicle_id) {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.filter.vehicle_id = vehicle_id;
      this.$http
        .post(`/supplies/period/report/provider`, this.filter)
        .then((r) => {
          this.$http
            .get(`/prefectures/${this.filter.prefecture_id}`)
            .then((p) => {
              this.prefecture = p.data[0];
              this.$http
                .get(`/providers/${this.filter.provider_id}`)
                .then((pp) => {
                  this.provider = pp.data[0];
                  this.dataReport = r.data;
                  this.showPrintPageOne = true;
                  // this.$refs.allComponent.initialize();
                  setTimeout(() => {
                    this.loader = false;
                    this.$store.dispatch("setLoader", this.loader);
                  }, 1000);
                });
            });
        });
    },
    rollbackInvoice() {
      let c = confirm(
        "Deseja desfaturar o faturamento completo do periodo selecionado?"
      );
      if (c) {
        this.$http
          .post(`/billings/rollbackinvoice`, this.filter)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.search();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    printAllBillings() {
      const url = `/billings/report/all/${this.filter.startDate}/${this.filter.endDate}/${this.filter.prefecture_id}/${this.filter.provider_id}/${this.filter.vehicle_id || 0}`;
      window.open(this.$router.resolve({ path: url }).href, "_blank");
    },
    searchBillings() {
      this.dataBillings = [];
      this.totals.billing_price = 0;
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      if (!this.filter.vehicle_id) {
        this.filter.vehicle_id = null;
      }
      const newFilter = {...this.filter}
      delete newFilter.filterDate;
      this.$http
        .post("/billings", newFilter)
        .then((r) => {
          this.dataBillings = r.data;
          this.dataBillings.map((b) => {
            this.totals.billing_price += parseFloat(b.bill_total_value);
          });
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch((e) => {
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
          this.snack.show = true;
          this.snack.message = "Erro ao buscar faturamentos: " + (e.response?.data?.message || e.message);
          this.snack.color = "error";
        });
    },
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.filter.prefecture_id = this.$store.getters.getUserData.table_id;
      if (this.filter.prefecture_id) {
        this.getVehicles(this.filter.prefecture_id);
        this.getProviders(this.filter.prefecture_id);
      }

      this.$http.get("/prefectures").then((r) => (this.prefectures = r.data));
      this.$http
        .get(
          `/vehicles/prefectures/${this.$store.getters.getUserData.table_id}`
        )
        .then((r) => {
          this.vehicles = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        });
      // this.$http
      //   .get(
      //     `/prefectures/secretariats/${this.$store.getters.getUserData.table_id}`
      //   )
      //   .then(r => {
      //     this.prefectures_sec = r.data;
      //     this.prefectures_sec.push({ sec_id: "TODOS", sec_name: "TODOS" });
      //   });
    },
    getVehicles(prefecture_id) {
      this.$http.get(`/vehicles/supplies/${prefecture_id}`).then((r) => {
        this.vehicles = r.data;
      });
    },
    getProviders(prefecture_id) {
      this.$http
        .get(`/providers/prefecture/${prefecture_id}`)
        .then((r) => (this.providers = r.data));
    },
    getSubSec(sec_id) {
      if (sec_id !== "TODOS") {
        this.$http.get(`/prefectures/subsectors/${sec_id}`).then((r) => {
          this.prefectures_subsectors = r.data;
          this.prefectures_subsectors.push({
            subsec_id: "TODOS",
            subsec_desc: "TODOS",
          });
        });
      }
    },
    invoice() {
      let c = confirm("Deseja faturar os abastecimentos?");
      if (c) {
        this.filter.nfe = false;
        //FATURAR
        this.$http
          .post(`/billings/invoice`, this.filter)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result ? r.data.result : r.data.error;
            this.snack.color = r.data.result ? "success" : "error";
            this.search();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    invoiceNFE() {
      let c = confirm("Deseja faturar os abastecimentos?");
      if (c) {
        //FATURAR
        this.filter.nfe = true;
        this.$http
          .post(`/billings/invoice/nfe`, this.filter)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result ? r.data.result : r.data.error;
            this.snack.color = r.data.result ? "success" : "error";
            this.search();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/drivers/${id}`)
          .then((r) => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.search();
          })
          .catch((e) => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    },
    abrirFoto(foto) {
      const baseURL = "https://api.sigfrotagov.com.br/uploads/";

      window.open(baseURL + foto?.replace('https://api.sigfrotagov.com.br/uploads/', ''), "_blank");
    },
  },
};
</script>

<style scoped>
.top-bar {
  padding: 30px 15px;
}
.title {
  text-transform: uppercase;
  font-weight: 400;
}
.maxPage {
  width: 100%;
}
.search-field {
  margin-top: 10px;
}
.space {
  margin: 0 10px;
}
</style>